// Quote templates modal (Session AAT, QUOTING DEPTH Loop 4). Opened from the office calculator.
// Lists saved templates (config only, no customer/site), lets the office apply one to the current
// calculator ("New quote from template" / load a shape), save the current configuration as a new
// template, and delete a template (two-step ConfirmButton). Admin/office.
import { useEffect, useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { QuoteTemplate } from "../../lib/api";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";

export function QuoteTemplatesModal({
  currentConfig,
  onApply,
  onClose,
}: {
  // The current calculator configuration (identity stripped) to offer as "Save as template".
  currentConfig: Record<string, unknown>;
  // Load a template's config into the calculator.
  onApply: (config: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const [templates, setTemplates] = useState<QuoteTemplate[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoadError(false);
    try {
      setTemplates(await api.quoteTemplates.list());
    } catch {
      setLoadError(true);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function save() {
    const n = name.trim();
    if (!n) return;
    setSaving(true);
    setError(null);
    try {
      await api.quoteTemplates.create(n, currentConfig);
      setName("");
      await load();
    } catch (err) {
      setError(err instanceof ApiError && err.code === "duplicate" ? "A template with that name already exists." : "Could not save the template. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(t: QuoteTemplate) {
    setBusyId(t.id);
    setError(null);
    try {
      await api.quoteTemplates.delete(t.id);
      await load();
    } catch {
      setError("Could not delete the template. Please try again.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Quote templates" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-lg text-ele-blue">Quote templates</h2>
        <p className="mt-1 text-sm text-ele-body">A template saves the lift configuration and tier (no customer details). Use one to start a new quote from a common shape.</p>

        <div className="mt-4 flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void save()}
            maxLength={80}
            placeholder="Save current setup as... (e.g. Standard MRL quarterly)"
            className="flex-1 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
          />
          <Button size="sm" onClick={save} disabled={saving || name.trim() === ""}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>

        {error && <p role="alert" className="mt-3 text-sm text-ele-danger">{error}</p>}

        <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
          {loadError ? (
            <p role="alert" className="text-sm text-ele-strong">Could not load the templates.</p>
          ) : templates == null ? (
            <Loader shape="inline" />
          ) : templates.length === 0 ? (
            <p className="text-sm text-ele-body">No templates yet. Save the current setup above to create one.</p>
          ) : (
            templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 rounded-ele border border-ele-purple-accent px-3 py-2">
                <span className="min-w-0 truncate text-sm text-ele-strong">{t.name}</span>
                <div className="flex shrink-0 items-center gap-3">
                  <button type="button" onClick={() => { onApply(t.config); onClose(); }} className="text-xs text-ele-green underline hover:text-ele-strong">
                    Use
                  </button>
                  <ConfirmButton
                    onConfirm={() => remove(t)}
                    busy={busyId === t.id}
                    disabled={busyId === t.id}
                    className="text-xs text-ele-body hover:text-ele-danger"
                    label="Delete"
                  />
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-5 flex justify-end">
          <button type="button" onClick={onClose} className="rounded-ele border border-ele-purple-accent px-4 py-2 text-sm">Done</button>
        </div>
      </div>
    </div>
  );
}
